import type { VisitorDerivedStatus } from './liveViewModel';

export const DEFAULT_LIVE_VIEW_PAGE_SIZE = 25;

export type LiveViewStatusFilter = VisitorDerivedStatus;
export type LiveViewLastSeenFilter = 'all' | 'today' | 'last7days' | 'last30days';
export type LiveViewSortOrder = 'asc' | 'desc';
export type LiveViewSortField = 'last_seen_at' | 'first_seen_at' | 'conversation_count';

type BuildVisitorProfileParamsArgs = {
  page?: number;
  perPage?: number;
  search?: string;
  statuses?: string[];
  lastSeenFilter?: string;
  firstSeenFilter?: string;
  identityType?: string;
  hasHistory?: string;
  humanAgentIds?: string[];
  sortBy?: LiveViewSortField;
  sortOrder?: LiveViewSortOrder;
  includeActivity?: boolean;
};

function mapLastSeenFilterToApi(value: string | undefined): string | null {
  switch (value) {
    case 'today':
      return 'today';
    case 'last7days':
      return 'last_7_days';
    case 'last30days':
      return 'last_30_days';
    default:
      return null;
  }
}

function appendDateRange(
  params: URLSearchParams,
  prefix: 'first_seen' | 'last_seen',
  value: string | undefined,
): void {
  if (!value || value === 'all') return;
  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  if (value === 'last7days') start.setDate(start.getDate() - 6);
  else if (value === 'last30days') start.setDate(start.getDate() - 29);
  else if (value !== 'today') return;
  params.set(`${prefix}_from`, start.toISOString());
}

export function buildVisitorProfileParams({
  page = 1,
  perPage = DEFAULT_LIVE_VIEW_PAGE_SIZE,
  search = '',
  statuses = [],
  lastSeenFilter = 'all',
  firstSeenFilter = 'all',
  identityType = 'all',
  hasHistory = 'all',
  humanAgentIds = [],
  sortBy = 'last_seen_at',
  sortOrder = 'desc',
  includeActivity = false,
}: BuildVisitorProfileParamsArgs): URLSearchParams {
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
    sort_by: sortBy,
    sort_order: sortOrder,
    include_activity: String(includeActivity),
  });

  if (search.trim()) {
    params.set('search', search.trim());
  }
  statuses.forEach((status) => params.append('status', status));
  humanAgentIds.forEach((id) => params.append('human_agent_ids', id));
  if (identityType && identityType !== 'all') params.set('identity_type', identityType);
  if (hasHistory && hasHistory !== 'all') {
    params.set('has_conversation_history', hasHistory);
  }

  const lastSeenRange = mapLastSeenFilterToApi(lastSeenFilter);
  if (lastSeenRange) {
    params.set('last_seen_range', lastSeenRange);
  }
  appendDateRange(params, 'first_seen', firstSeenFilter);

  return params;
}
